import { uiText, useUiLanguage } from '@/i18n/interface';

import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import CurrentBeltStatus from './belt-requirements/CurrentBeltStatus';
import RequirementsList from './belt-requirements/RequirementsList';
import LoadingState from './belt-requirements/LoadingState';
import NoBeltAssigned from './belt-requirements/NoBeltAssigned';
import { usePromotionEligibility } from '@/hooks/usePromotionEligibility';
import DynamicBeltDisplay from './belt-requirements/DynamicBeltDisplay';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Circle, Clock, Users, Trophy } from 'lucide-react';
import ErrorBoundary from './ErrorBoundary';

interface BeltLevel {
  id: string;
  name: string;
  color: string;
  order_sequence: number;
  requirements: any;
}

interface StudentRequirement {
  id: string;
  requirement_name: string;
  completed: boolean;
  completed_date: string | null;
  notes: string | null;
  progress_percentage: number;
  points_earned: number;
  category?: string;
  description?: string;
  title?: string;
  points?: number;
}

const StudentBeltRequirements = () => {
  useUiLanguage();
  const { profile } = useAuth();
  const {
    attendanceCount,
    isEligibleForPromotion,
    requiredAttendance,
    isLoading: eligibilityLoading
  } = usePromotionEligibility();

  const [currentBelt, setCurrentBelt] = useState<BeltLevel | null>(null);
  const [nextBelt, setNextBelt] = useState<BeltLevel | null>(null);
  const [requirements, setRequirements] = useState<StudentRequirement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Move useMemo to top level to ensure consistent hook order
  const safeRequirements = useMemo(() => {
    return Array.isArray(requirements) ? requirements.filter(Boolean) : [];
  }, [requirements]);

  const { completedRequirements, totalRequirements } = useMemo(() => ({
    completedRequirements: safeRequirements.filter(req => req?.completed).length,
    totalRequirements: safeRequirements.length
  }), [safeRequirements]);

  const fetchBeltData = async () => {
    if (!profile?.belt_level_id || !profile?.dojo_id) {
      console.log('No belt level or dojo ID found');
      setIsLoading(false);
      return;
    }

    try {
      setError(null);
      setIsLoading(true);

      console.log('Fetching belt data for profile:', profile.id);

      // Fetch current belt
      const { data: currentBeltData, error: currentBeltError } = await supabase
        .from('belt_levels')
        .select('*')
        .eq('id', profile.belt_level_id)
        .single();

      if (currentBeltError) {
        console.error('Error fetching current belt:', currentBeltError);
        throw new Error(uiText("Failed to load current belt information"));
      }

      console.log('Current belt loaded:', currentBeltData);
      setCurrentBelt(currentBeltData);

      // Fetch next belt
      const { data: nextBeltData, error: nextBeltError } = await supabase
        .from('belt_levels')
        .select('*')
        .eq('dojo_id', profile.dojo_id)
        .eq('order_sequence', currentBeltData.order_sequence + 1)
        .single();

      if (nextBeltError && nextBeltError.code !== 'PGRST116') {
        console.error('Error fetching next belt:', nextBeltError);
        // Don't throw error if there's no next belt - student might be at highest level
      } else if (nextBeltData) {
        console.log('Next belt loaded:', nextBeltData);
        setNextBelt(nextBeltData);
        await fetchRequirements(nextBeltData);
      } else {
        console.log('Student is at highest belt level');
        setNextBelt(null);
        setRequirements([]);
      }

    } catch (error) {
      console.error('Error in fetchBeltData:', error);
      setError(error instanceof Error ? error.message : 'Failed to load belt information');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRequirements = async (targetBelt: BeltLevel) => {
    if (!profile?.id || !targetBelt?.id) {
      console.log('Missing profile or target belt');
      return;
    }

    try {
      console.log('Fetching requirements for belt:', targetBelt.name);

      // Try to get requirements from belt_requirements table first
      const { data: beltRequirements, error: reqError } = await supabase
        .from('belt_requirements')
        .select('*')
        .eq('belt_level_id', targetBelt.id)
        .order('order_index');

      let baseRequirements = [];

      if (!reqError && beltRequirements && beltRequirements.length > 0) {
        console.log('Found', beltRequirements.length, 'requirements in belt_requirements table');
        baseRequirements = beltRequirements.map((req) => ({
          id: req.id,
          requirement_name: req.title || 'Unnamed Requirement',
          category: req.category || 'general',
          description: req.description || '',
          points: req.points || 1,
          completed: false,
          completed_date: null,
          notes: null,
          progress_percentage: 0,
          points_earned: 0
        }));
      } else {
        // Fallback to JSON requirements
        console.log('Using JSON requirements from belt_levels table');
        const jsonRequirements = targetBelt.requirements;

        if (Array.isArray(jsonRequirements)) {
          baseRequirements = jsonRequirements.map((req, index) => ({
            id: `json_req_${index}`,
            requirement_name: req?.title || req?.requirement_name || req?.name || `Requirement ${index + 1}`,
            category: req?.category || 'general',
            description: req?.description || '',
            points: req?.points || 1,
            completed: false,
            completed_date: null,
            notes: null,
            progress_percentage: 0,
            points_earned: 0
          }));
        } else {
          console.log('No valid requirements found');
          baseRequirements = [];
        }
      }

      if (baseRequirements.length === 0) {
        console.log('No requirements found for belt:', targetBelt.name);
        setRequirements([]);
        return;
      }

      // Get student progress
      const { data: studentProgress, error: progressError } = await supabase
        .from('student_requirements')
        .select('*')
        .eq('student_id', profile.id)
        .eq('belt_level_id', targetBelt.id);

      if (progressError) {
        console.error('Error fetching student progress:', progressError);
      }

      // Create progress map
      const progressMap = new Map();
      if (studentProgress && Array.isArray(studentProgress)) {
        studentProgress.forEach(p => {
          if (p && p.requirement_name) {
            progressMap.set(p.requirement_name, p);
          }
        });
      }

      // Merge requirements with progress
      const finalRequirements = baseRequirements.map(req => {
        const progress = progressMap.get(req.requirement_name);
        return {
          ...req,
          completed: Boolean(progress?.completed),
          completed_date: progress?.completed_date || null,
          notes: progress?.notes || null,
          progress_percentage: progress?.progress_percentage || 0,
          points_earned: progress?.points_earned || 0
        };
      });

      console.log('Final requirements:', finalRequirements);
      setRequirements(finalRequirements);

    } catch (error) {
      console.error('Error fetching requirements:', error);
      setError(uiText("Failed to load requirements"));
    }
  };

  useEffect(() => {
    if (profile?.belt_level_id && profile?.dojo_id) {
      fetchBeltData();
    } else {
      setIsLoading(false);
    }
  }, [profile?.belt_level_id, profile?.dojo_id]);

  const toggleRequirement = async (requirementId: string) => {
    if (!profile?.id || !nextBelt?.id) {
      console.error('Missing profile or next belt data');
      toast({
        title: uiText("Error"),
        description: uiText("Cannot update requirement. Please refresh the page."),
        variant: "destructive"
      });
      return;
    }

    const requirement = requirements.find(req => req?.id === requirementId);
    if (!requirement || !requirement.requirement_name) {
      console.error('Requirement not found or invalid:', requirementId);
      toast({
        title: uiText("Error"),
        description: uiText("Requirement not found. Please refresh the page."),
        variant: "destructive"
      });
      return;
    }

    const newCompletedStatus = !requirement.completed;
    const requirementName = requirement.requirement_name;

    try {
      if (newCompletedStatus) {
        // Mark as completed
        const { error } = await supabase
          .from('student_requirements')
          .upsert({
            student_id: profile.id,
            belt_level_id: nextBelt.id,
            requirement_name: requirementName,
            completed: true,
            completed_date: new Date().toISOString(),
            points_earned: requirement.points || 1,
            progress_percentage: 100,
          }, {
            onConflict: 'student_id,belt_level_id,requirement_name'
          });

        if (error) {
          console.error('Error marking requirement as completed:', error);
          throw error;
        }

        toast({
          title: uiText("Requirement Completed"),
          description: uiText("{{value0}} marked as completed.", { value0: requirementName }),
        });
      } else {
        // Mark as not completed
        const { error } = await supabase
          .from('student_requirements')
          .delete()
          .eq('student_id', profile.id)
          .eq('belt_level_id', nextBelt.id)
          .eq('requirement_name', requirementName);

        if (error) {
          console.error('Error marking requirement as not completed:', error);
          throw error;
        }

        toast({
          title: uiText("Requirement Updated"),
          description: uiText("{{value0}} marked as not completed.", { value0: requirementName }),
        });
      }

      // Update local state
      setRequirements(prev =>
        prev.map(req =>
          req?.id === requirementId
            ? {
                ...req,
                completed: newCompletedStatus,
                completed_date: newCompletedStatus ? new Date().toISOString() : null,
                progress_percentage: newCompletedStatus ? 100 : 0,
                points_earned: newCompletedStatus ? (req.points || 1) : 0
              }
            : req
        ).filter(Boolean) // Remove any null/undefined items
      );

    } catch (error) {
      console.error('Error in toggleRequirement:', error);
      toast({
        title: uiText("Error"),
        description: uiText("Failed to update requirement. Please try again."),
        variant: "destructive"
      });
    }
  };

  if (isLoading || eligibilityLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="text-red-600">{uiText("Error Loading Requirements")}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              fetchBeltData();
            }}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            {uiText("Try Again")}</button>
        </CardContent>
      </Card>
    );
  }

  if (!currentBelt) {
    return <NoBeltAssigned />;
  }

  return (
    <ErrorBoundary>
      <div className="space-y-6">
        <DynamicBeltDisplay currentBelt={currentBelt} nextBelt={nextBelt} />

        <CurrentBeltStatus
          currentBelt={currentBelt}
          nextBelt={nextBelt}
          attendanceCount={attendanceCount}
          requiredAttendance={requiredAttendance}
          isEligibleForPromotion={isEligibleForPromotion}
          completedRequirements={completedRequirements}
          totalRequirements={totalRequirements}
        />

        <RequirementsList
          requirements={safeRequirements}
          isEligibleForPromotion={isEligibleForPromotion}
          onToggleRequirement={toggleRequirement}
        />
      </div>
    </ErrorBoundary>
  );
};

export default StudentBeltRequirements;
